import React, { useState, useEffect, useRef } from 'react';
import Container from '../../layout/Container';

const Stats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);

  const stats = [
    {
      value: '100%',
      label: 'Dedicação em cada projeto',
      suffix: '',
    },
    {
      value: '24/7',
      label: 'Suporte e acompanhamento',
      suffix: '',
    },
    {
      value: '∞',
      label: 'Possibilidades com IA',
      suffix: '',
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e) => {
    if (sectionRef.current) {
      const rect = sectionRef.current.getBoundingClientRect();
      setMousePosition({
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
        y: ((e.clientY - rect.top) / rect.height - 0.5) * 20,
      });
    }
  };

  return (
    <section 
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="py-12 md:py-16 relative overflow-hidden"
    >
      {/* Background com efeitos */}
      <div className="absolute inset-0">
        {/* Grid animado */}
        <div 
          className="absolute inset-0 opacity-5 transition-transform duration-500"
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Glows flutuantes */}
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse-slow"
          style={{
            transform: `translate(${mousePosition.x * 2}px, ${mousePosition.y * 2}px)`,
          }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse-slow"
          style={{
            transform: `translate(${-mousePosition.x * 2}px, ${-mousePosition.y * 2}px)`,
            animationDelay: '1s',
          }}
        />
      </div>

      <Container>
        {/* Card principal com glassmorphism avançado */}
        <div 
          className={`
            relative overflow-hidden
            bg-gradient-to-br from-gray-900/50 via-gray-800/30 to-gray-900/50 
            backdrop-blur-xl 
            border border-gray-700/50
            rounded-3xl
            p-6 md:p-10
            transition-all duration-1000
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
          `}
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)',
          }}
        >
          {/* Borda animada com gradiente */}
          <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700">
            <div 
              className="absolute inset-0 rounded-3xl"
              style={{
                background: `conic-gradient(from ${mousePosition.x * 10}deg, transparent, rgba(16, 185, 129, 0.3), transparent 50%)`,
                padding: '2px',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
          </div>

          {/* Shimmer effect que percorre o card */}
          <div className="absolute inset-0 overflow-hidden rounded-3xl">
            <div 
              className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full transition-transform duration-3000 ${isVisible ? 'translate-x-full' : ''}`}
              style={{
                transform: 'skewX(-20deg)',
              }}
            />
          </div>

          {/* Grid de stats */}
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="group text-center md:text-left relative"
                style={{
                  animationDelay: `${index * 0.2}s`,
                }}
              >
                {/* Card interno com hover effect */}
                <div className="relative p-4 rounded-2xl transition-all duration-500 hover:bg-gray-800/30">
                  {/* Glow ao hover */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl bg-emerald-500/20" />

                  {/* Conteúdo */}
                  <div className="relative">
                    {/* Valor animado */}
                    <div 
                      className={`
                        text-4xl md:text-5xl font-bold mb-3
                        text-emerald-500
                        transition-all duration-700
                        ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}
                        group-hover:scale-110
                      `}
                      style={{
                        transitionDelay: `${index * 0.1 + 0.3}s`,
                      }}
                    >
                      {stat.value}
                      {stat.suffix && <span className="text-2xl">{stat.suffix}</span>}
                    </div>

                    {/* Label */}
                    <p 
                      className={`
                        text-gray-400 text-sm md:text-base leading-relaxed
                        transition-all duration-700
                        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                        group-hover:text-gray-300
                      `}
                      style={{
                        transitionDelay: `${index * 0.1 + 0.5}s`,
                      }}
                    >
                      {stat.label}
                    </p>

                    {/* Barra decorativa animada */}
                    <div className="mt-3 h-0.5 w-0 group-hover:w-full transition-all duration-700 bg-gradient-to-r from-emerald-500 to-transparent rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Stats;